import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Circle, PlayCircle, Zap, ArrowUpRight, Lock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import { JOURNEYS, ALL_FEATURES, PLAN_RANK, featureHref } from "../../lib/guideCatalog";
import { tr } from "../../lib/i18n";

const STATE = {
  done: { label: "Done", Icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-500/10 border-emerald-500/30" },
  in_use: { label: "In use", Icon: PlayCircle, cls: "text-sky-700 bg-sky-500/10 border-sky-500/30" },
  ready: { label: "Always on", Icon: Zap, cls: "text-amber-700 bg-amber-500/10 border-amber-500/30" },
  todo: { label: "Not started", Icon: Circle, cls: "text-muted-foreground bg-muted border-border" },
};

export const visibleFeatures = (role) => ALL_FEATURES.filter((f) => !role || f.roles.includes(role));

export const FeatureCard = ({ f, status, plan, personal, index }) => {
  const st = status ? STATE[status.state] || STATE.todo : null;
  const locked = personal && f.plan && PLAN_RANK[plan || "free"] < PLAN_RANK[f.plan];
  return (
    <motion.li initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(index, 8) * 0.03 }} data-testid={`guide-feature-${f.id}`} className="rounded-2xl border border-border bg-card p-4 flex flex-col">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-9 w-9 rounded-xl bg-primary/10 text-primary shrink-0"><f.Icon className="h-[18px] w-[18px]" /></span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold">{f.title}</h3>
            {f.plan && <span data-testid={`guide-plan-${f.id}`} className="rounded-full border border-primary/30 bg-primary/5 text-primary px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider">{f.plan}</span>}
          </div>
          <p className="mt-1 text-xs text-muted-foreground leading-relaxed">{f.what}</p>
        </div>
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground"><span className="font-mono uppercase tracking-wider text-[10px] text-primary">{tr("How ·")} </span>{f.how}</p>
      <div className="mt-3 flex items-center justify-between gap-2 pt-3 border-t border-border/70">
        {st ? (
          <TooltipProvider delayDuration={200}><Tooltip><TooltipTrigger asChild>
            <span data-testid={`guide-status-${f.id}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${st.cls}`}><st.Icon className="h-3 w-3" /> {st.label}</span>
          </TooltipTrigger>{status.note && <TooltipContent className="text-xs max-w-xs">{status.note}</TooltipContent>}</Tooltip></TooltipProvider>
        ) : <span />}
        {status?.note && <span className="text-[11px] text-muted-foreground truncate flex-1 hidden sm:block">{status.note}</span>}
        {locked ? (
          <Link to="/pricing" data-testid={`guide-upgrade-${f.id}`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline shrink-0"><Lock className="h-3 w-3" /> {tr("Upgrade to")} {f.plan}</Link>
        ) : personal ? (
          <Link to={featureHref(f)} data-testid={`guide-open-${f.id}`} className="inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground px-3 py-1 text-xs font-medium hover:bg-primary/90 shrink-0">{tr("Open")} <ArrowUpRight className="h-3 w-3" /></Link>
        ) : null}
      </div>
    </motion.li>
  );
};

export const JourneyNav = ({ counts }) => (
  <nav className="flex gap-2 overflow-x-auto pb-1 min-w-0 max-w-full" data-testid="guide-journey-nav">
    {JOURNEYS.map((j, i) => (
      <a key={j.id} href={`#${j.id}`} className="shrink-0 rounded-full border border-border bg-card px-3 py-1.5 text-xs hover:border-primary hover:text-primary transition-colors">
        <span className="font-mono text-[10px] text-primary mr-1.5">{i + 1}</span>{j.label}{counts?.[j.id] != null && <span className="text-muted-foreground"> · {counts[j.id]}</span>}
      </a>
    ))}
  </nav>
);

export const JourneySection = ({ j, features, statuses, plan, personal }) => (
  <section id={j.id} data-testid={`guide-journey-${j.id}`} className="scroll-mt-24">
    <div className="flex items-end gap-3 mb-4">
      <span className="font-serif text-4xl text-primary/40 leading-none">{String(JOURNEYS.findIndex((x) => x.id === j.id) + 1).padStart(2, "0")}</span>
      <div><h2 className="font-serif text-2xl leading-none">{j.label}</h2><p className="text-xs text-muted-foreground mt-1">{j.blurb}</p></div>
    </div>
    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{features.map((f, i) => <FeatureCard key={f.id} f={f} status={statuses?.[f.id]} plan={plan} personal={personal} index={i} />)}</ul>
  </section>
);
